/* eslint-disable max-len */
import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch, connect } from 'react-redux'
import {
  CContainer,
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CHeader,
  CHeaderNav,
  CHeaderToggler,
  CNavItem,
  useColorModes,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CForm,
  CFormInput,
  CFormLabel,
  CAlert,
  CAvatar,
  CInputGroup,
  CInputGroupText,
  CCard,
  CCardBody,
  CProgress,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilContrast,
  cilMenu,
  cilMoon,
  cilSun,
  cilGlobeAlt,
  cilLockLocked,
  cilAccountLogout,
  cilLockUnlocked,
  cilShieldAlt,
  cilCheckCircle,
  cilWarning,
  cilBook,
} from '@coreui/icons'
import { useLanguage } from '../context/LanguageContext'
import { useTranslation } from '../hooks/useTranslation'
import { changeUserPasswordRequestAction } from '../store/users/usersActions'
import './AppHeader.scss'

const AppHeader = (props) => {
  const { changeUserPasswordRequest, changeUserPasswordData, changeUserPasswordError } = props
  
  const headerRef = useRef()
  const { colorMode, setColorMode } = useColorModes('coreui-free-react-admin-template-theme')
  const { currentLanguage, changeLanguage } = useLanguage()
  const { t } = useTranslation()

  const dispatch = useDispatch()
  const sidebarShow = useSelector((state) => state.sidebar.sidebarShow)

  // Modal state
  const [changePasswordModal, setChangePasswordModal] = useState(false)
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  
  // Password visibility states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  
  // Password strength
  const [passwordStrength, setPasswordStrength] = useState(0)
  const [passwordStrengthText, setPasswordStrengthText] = useState('')

  // Get user data
  const getUserData = () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}')
      return {
        name: userData?.username || 'User',
      }
    } catch {
      return {
        name: 'User',
      }
    }
  }

  const userData = getUserData()
  const userInitials = userData.name?.charAt(0)?.toUpperCase() || 'U'

  // Watch for Redux state changes
  useEffect(() => {
    if (changeUserPasswordData && changeUserPasswordData.Result && changeUserPasswordData.Result.ErrorCode === 0) {
      setPasswordSuccess(t('passwordChangedSuccessfully'))
      setIsLoading(false)
      setTimeout(() => {
        setChangePasswordModal(false)
        handleModalClose()
      }, 2000)
    }
  }, [changeUserPasswordData, t])

  useEffect(() => {
    if (changeUserPasswordError) {
      setIsLoading(false)
      const errorMessage = changeUserPasswordError.response?.data?.Result?.ErrorDescription || 
                          changeUserPasswordError.message ||
                          t('passwordChangeError')
      setPasswordError(errorMessage)
    }
  }, [changeUserPasswordError, t])

  // Password strength calculator
  const calculatePasswordStrength = (password) => {
    let strength = 0
    let strengthText = ''
    
    if (password.length >= 6) strength += 20
    if (password.length >= 8) strength += 20
    if (/[a-z]/.test(password)) strength += 15
    if (/[A-Z]/.test(password)) strength += 15
    if (/[0-9]/.test(password)) strength += 15
    if (/[^a-zA-Z0-9]/.test(password)) strength += 15
    
    if (strength <= 30) {
      strengthText = t('weakPassword')
    } else if (strength <= 60) {
      strengthText = t('mediumPassword')
    } else if (strength <= 80) {
      strengthText = t('strongPassword')
    } else {
      strengthText = t('veryStrongPassword')
    }
    
    return { strength, strengthText }
  }

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 30) return 'danger'
    if (passwordStrength <= 60) return 'warning'
    if (passwordStrength <= 80) return 'info'
    return 'success'
  }

  useEffect(() => {
    document.addEventListener('scroll', () => {
      headerRef.current &&
        headerRef.current.classList.toggle('shadow-sm', document.documentElement.scrollTop > 0)
    })
  }, [])

  const toggleSidebar = () => {
    dispatch({ type: 'set', sidebarShow: !sidebarShow })
  }

  const handleChangePasswordClick = () => {
    setChangePasswordModal(true)
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })
    setPasswordError('')
    setPasswordSuccess('')
    setPasswordStrength(0)
    setPasswordStrengthText('')
    setShowCurrentPassword(false)
    setShowNewPassword(false)
    setShowConfirmPassword(false)
  }

  const handlePasswordFormChange = (field, value) => {
    setPasswordForm(prev => ({
      ...prev,
      [field]: value
    }))
    
    if (field === 'newPassword') {
      const { strength, strengthText } = calculatePasswordStrength(value)
      setPasswordStrength(strength)
      setPasswordStrengthText(strengthText)
    }
    
    if (passwordError) {
      setPasswordError('')
    }
  }

  const validatePasswordForm = () => {
    if (!passwordForm.currentPassword) {
      setPasswordError(t('currentPasswordRequired'))
      return false
    }
    if (!passwordForm.newPassword) {
      setPasswordError(t('newPasswordRequired'))
      return false
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError(t('passwordTooShort'))
      return false
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError(t('passwordsNotMatch'))
      return false
    }
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordError(t('newPasswordSameAsCurrent'))
      return false
    }
    return true
  }

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault()
    
    if (!validatePasswordForm()) {
      return
    }

    setIsLoading(true)
    setPasswordError('')

    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}')
      const sessionUserId = localStorage.getItem('sessionUserId') || userData?.sessionUserId

      if (!sessionUserId || sessionUserId === 'undefined') {
        setPasswordError(t('userSessionNotFound'))
        setIsLoading(false)
        return
      }

      const payload = {
        SessionUserId: String(sessionUserId),
        Password: passwordForm.currentPassword,
        NewPassword: passwordForm.newPassword,
        NewPasswordAgain: passwordForm.confirmPassword
      }

      // Use Redux saga
      changeUserPasswordRequest({ data: payload })

    } catch (error) {
      setPasswordError(t('passwordChangeError'))
      setIsLoading(false)
    }
  }

  const handleModalClose = () => {
    setChangePasswordModal(false)
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })
    setPasswordError('')
    setPasswordSuccess('')
    setShowCurrentPassword(false)
    setShowNewPassword(false)
    setShowConfirmPassword(false)
    setPasswordStrength(0)
    setPasswordStrengthText('')
    setIsLoading(false)
  }

  const handleLogout = () => {
    localStorage.removeItem('user')
    localStorage.removeItem('sessionUserId')
    localStorage.removeItem('token')
    localStorage.removeItem('authToken')
    window.location.href = '/login'
  }

  // Language toggle handler
  const handleLanguageToggle = () => {
    const newLanguage = currentLanguage === 'tr' ? 'en' : 'tr'
    changeLanguage(newLanguage)
  }

  // User Guide handler
  const handleUserGuideClick = () => {
    window.open('https://backoffice.fabcotekstil.com.tr/userguide/docs/', '_blank')
  }

  return (
    <>
      <CHeader position="sticky" className="app-header mb-4 p-0" ref={headerRef}>
        <CContainer className="border-bottom px-4" fluid>
          <CHeaderToggler
            onClick={toggleSidebar}
            style={{ marginInlineStart: '-14px' }}
          >
            <CIcon icon={cilMenu} size="lg" />
          </CHeaderToggler>
          <CHeaderNav className="d-none d-md-flex">
            <CNavItem>
            </CNavItem>
          </CHeaderNav>
          <CHeaderNav>
            <li className="nav-item py-1">
              <div className="vr h-100 mx-2 text-body text-opacity-75"></div>
            </li>
            
            {/* User Menu */}
            <CDropdown variant="nav-item" placement="bottom-end">
              <CDropdownToggle caret={false} className="d-flex align-items-center">
                <CAvatar 
                  color="primary" 
                  textColor="white"
                  size="sm"
                  className="user-avatar me-2"
                >
                  {userInitials}
                </CAvatar>
                <span className="d-none d-md-inline">
                  {userData.name}
                </span>
              </CDropdownToggle>
              <CDropdownMenu>
                <CDropdownItem
                  className="d-flex align-items-center"
                  as="button"
                  type="button"
                  onClick={handleChangePasswordClick}
                >
                  <CIcon className="me-2" icon={cilLockLocked} size="sm" />
                  {t('changePassword')}
                </CDropdownItem>
                <CDropdownItem
                  className="d-flex align-items-center text-warning"
                  as="button"
                  type="button"
                  onClick={handleLogout}
                >
                  <CIcon className="me-2" icon={cilAccountLogout} size="sm" />
                  {t('logout')}
                </CDropdownItem>
              </CDropdownMenu>
            </CDropdown>

            <li className="nav-item py-1">
              <div className="vr h-100 mx-2 text-body text-opacity-75"></div>
            </li>

            {/* User Guide Button */}
            <CNavItem className="nav-item">
              <CButton
                variant="ghost"
                className="user-guide-btn d-flex align-items-center"
                onClick={handleUserGuideClick}
                title={t('userGuide') || 'User Guide'}
              >
                <CIcon icon={cilBook} size="lg" className="me-1" />
                <span className="d-none d-lg-inline user-guide-text">
                  {t('userGuide') || 'User Guide'}
                </span>
              </CButton>
            </CNavItem>
            
            <li className="nav-item py-1">
              <div className="vr h-100 mx-2 text-body text-opacity-75"></div>
            </li>
            
            {/* Language Menu */}
            <CDropdown variant="nav-item" placement="bottom-end">
              <CDropdownToggle caret={false}>
                <CIcon icon={cilGlobeAlt} size="lg" />
              </CDropdownToggle>
              <CDropdownMenu>
                <CDropdownItem
                  active={currentLanguage === 'en'}
                  className="d-flex align-items-center"
                  as="button"
                  type="button"
                  onClick={() => changeLanguage('en')}
                >
                  🇺🇸 {t('english')}
                </CDropdownItem>
                <CDropdownItem
                  active={currentLanguage === 'tr'}
                  className="d-flex align-items-center"
                  as="button"
                  type="button"
                  onClick={() => changeLanguage('tr')}
                >
                  🇹🇷 {t('turkish')}
                </CDropdownItem>
              </CDropdownMenu>
            </CDropdown>
            
            <li className="nav-item py-1">
              <div className="vr h-100 mx-2 text-body text-opacity-75"></div>
            </li>
            
            {/* Theme Menu */}
            <CDropdown variant="nav-item" placement="bottom-end">
              <CDropdownToggle caret={false}>
                {colorMode === 'dark' ? (
                  <CIcon icon={cilMoon} size="lg" />
                ) : colorMode === 'auto' ? (
                  <CIcon icon={cilContrast} size="lg" />
                ) : (
                  <CIcon icon={cilSun} size="lg" />
                )}
              </CDropdownToggle>
              <CDropdownMenu>
                <CDropdownItem
                  active={colorMode === 'light'}
                  className="d-flex align-items-center"
                  as="button"
                  type="button"
                  onClick={() => setColorMode('light')}
                >
                  <CIcon className="me-2" icon={cilSun} size="lg" /> {t('light')}
                </CDropdownItem>
                <CDropdownItem
                  active={colorMode === 'dark'}
                  className="d-flex align-items-center"
                  as="button"
                  type="button"
                  onClick={() => setColorMode('dark')}
                >
                  <CIcon className="me-2" icon={cilMoon} size="lg" /> {t('dark')}
                </CDropdownItem>
                <CDropdownItem
                  active={colorMode === 'auto'}
                  className="d-flex align-items-center"
                  as="button"
                  type="button"
                  onClick={() => setColorMode('auto')}
                >
                  <CIcon className="me-2" icon={cilContrast} size="lg" /> {t('auto')}
                </CDropdownItem>
              </CDropdownMenu>
            </CDropdown>
            
            <li className="nav-item py-1">
              <div className="vr h-100 mx-2 text-body text-opacity-75"></div>
            </li>
          </CHeaderNav>
        </CContainer>
      </CHeader>

      {/* Modern Password Change Modal */}
      <CModal 
        visible={changePasswordModal} 
        onClose={handleModalClose} 
        alignment="center"
        size="lg"
        backdrop="static"
        className="password-change-modal"
      >
        <CModalHeader className="border-0 pb-4">
          <CModalTitle className="d-flex align-items-center">
            <CIcon className="me-2" icon={cilShieldAlt} size="lg" />
            {t('changePassword')}
          </CModalTitle>
        </CModalHeader>
        
        <CForm onSubmit={handleChangePasswordSubmit}>
          <CModalBody className="pt-1">
            <CCard className="password-form-card border-0 shadow-sm">
              <CCardBody>
                {passwordError && (
                  <CAlert color="danger" className="d-flex align-items-center mb-4">
                    <CIcon className="flex-shrink-0 me-2" icon={cilWarning} width={24} height={24} />
                    <div>{passwordError}</div>
                  </CAlert>
                )}
                
                {passwordSuccess && (
                  <CAlert color="success" className="d-flex align-items-center mb-4">
                    <CIcon className="flex-shrink-0 me-2" icon={cilCheckCircle} width={24} height={24} />
                    <div>{passwordSuccess}</div>
                  </CAlert>
                )}

                {/* Current Password */}
                <div className="password-input-group mb-4">
                  <CFormLabel htmlFor="currentPassword" className="fw-semibold">
                    {t('currentPassword')}
                  </CFormLabel>
                  <CInputGroup>
                    <CFormInput
                      type={showCurrentPassword ? "text" : "password"}
                      id="currentPassword"
                      placeholder={t('enterCurrentPassword')}
                      value={passwordForm.currentPassword}
                      onChange={(e) => handlePasswordFormChange('currentPassword', e.target.value)}
                      required
                      disabled={isLoading || passwordSuccess}
                      className="border-end-0"
                    />
                    <CInputGroupText 
                      className="bg-transparent border-start-0"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      style={{ cursor: 'pointer' }}
                    >
                      <CIcon icon={showCurrentPassword ? cilLockUnlocked : cilLockLocked} />
                    </CInputGroupText>
                  </CInputGroup>
                </div>

                {/* New Password */}
                <div className="password-input-group mb-3">
                  <CFormLabel htmlFor="newPassword" className="fw-semibold">
                    {t('newPassword')}
                  </CFormLabel>
                  <CInputGroup>
                    <CFormInput
                      type={showNewPassword ? "text" : "password"}
                      id="newPassword"
                      placeholder={t('enterNewPassword')}
                      value={passwordForm.newPassword}
                      onChange={(e) => handlePasswordFormChange('newPassword', e.target.value)}
                      required
                      disabled={isLoading || passwordSuccess}
                      className="border-end-0"
                    />
                    <CInputGroupText 
                      className="bg-transparent border-start-0"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      style={{ cursor: 'pointer' }}
                    >
                      <CIcon icon={showNewPassword ? cilLockUnlocked : cilLockLocked} />
                    </CInputGroupText>
                  </CInputGroup>
                  
                  {/* Password Strength Indicator */}
                  {passwordForm.newPassword && (
                    <div className="password-strength-indicator mt-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <small className="text-muted">
                          {t('passwordStrength')}:
                        </small>
                        <small className={`text-${getPasswordStrengthColor()} fw-semibold`}>
                          {passwordStrengthText}
                        </small>
                      </div>
                      <CProgress 
                        value={passwordStrength} 
                        color={getPasswordStrengthColor()} 
                        height={8}
                        className="mb-1"
                      />
                      <small className="text-muted">
                        {t('passwordRequirements')}
                      </small>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="password-input-group mb-3">
                  <CFormLabel htmlFor="confirmPassword" className="fw-semibold">
                    {t('confirmNewPassword')}
                  </CFormLabel>
                  <CInputGroup>
                    <CFormInput
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      placeholder={t('confirmNewPassword')}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => handlePasswordFormChange('confirmPassword', e.target.value)}
                      required
                      disabled={isLoading || passwordSuccess}
                      className={`border-end-0 ${
                        passwordForm.confirmPassword && 
                        passwordForm.newPassword !== passwordForm.confirmPassword 
                          ? 'is-invalid' : ''
                      }`}
                    />
                    <CInputGroupText 
                      className="bg-transparent border-start-0"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{ cursor: 'pointer' }}
                    >
                      <CIcon icon={showConfirmPassword ? cilLockUnlocked : cilLockLocked} />
                    </CInputGroupText>
                  </CInputGroup>
                  
                  {/* Password Match Indicator */}
                  {passwordForm.confirmPassword && (
                    <div className="mt-1">
                      {passwordForm.newPassword === passwordForm.confirmPassword ? (
                        <small className="text-success d-flex align-items-center">
                          <CIcon icon={cilCheckCircle} size="sm" className="me-1" />
                          {t('passwordsMatch')}
                        </small>
                      ) : (
                        <small className="text-danger d-flex align-items-center">
                          <CIcon icon={cilWarning} size="sm" className="me-1" />
                          {t('passwordsNotMatch')}
                        </small>
                      )}
                    </div>
                  )}
                </div>
              </CCardBody>
            </CCard>
          </CModalBody>
          
          <CModalFooter className="border-0 pt-0">
            <CButton 
              color="light" 
              onClick={handleModalClose}
              disabled={isLoading}
              className="me-2"
            >
              {t('cancel')}
            </CButton>
            <CButton 
              color="primary" 
              type="submit"
              disabled={isLoading || passwordSuccess || passwordStrength < 30}
              className="px-4"
            >
              {isLoading && (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              )}
              {isLoading ? t('changing') : t('changePassword')}
            </CButton>
          </CModalFooter>
        </CForm>
      </CModal>
    </>
  )
}

// Redux connection
const mapStateToProps = (state) => {
  return {
    changeUserPasswordData: state.usersReducer?.changeUserPasswordData || null,
    changeUserPasswordError: state.usersReducer?.changeUserPasswordError || null,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    changeUserPasswordRequest: (payload) => {
      dispatch(changeUserPasswordRequestAction(payload))
    },
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AppHeader)